import React, { FC, useCallback, useEffect, useReducer, useState } from 'react';

import cn from 'classnames';
import Dialog from 'components/Dialog';
import { Button, LoaderSpinner, PercentInput, TextField } from 'components/UtilElements';
import * as dt from 'core/datetime';
import { DATE_FORMAT } from 'globals/constants';
import { exportActivityDetails, useActivityDetailsAPI } from 'services/course-api';
import { toFixedIfFloat } from 'utils';

import { ReactComponent as BadgeIcon } from 'assets/badge.svg';
import { ReactComponent as ChatBubbleIcon } from 'assets/chat-bubble.svg';

const getStylesForScore = (score: number) => {
  if (score >= 80) return 'good';
  if (score >= 60) return 'ok';
  else return 'bad';
};

interface DiscussionParticipationsProps {
  isOpen: boolean;
  activityId: string;
  onClose(): void;
  onExport(): void;
}

interface SetAwardAction {
  type: 'SET_AWARD';
  userId: string;
  award: number | undefined;
}

const individualAwardReducer = (state: HashMap<number | undefined>, action: SetAwardAction) => {
  switch (action.type) {
    case 'SET_AWARD':
      return {
        ...state,
        [action.userId]: action.award,
      };
    default:
      throw new Error('Invalid action');
  }
};

const DiscussionParticipations: FC<DiscussionParticipationsProps> = ({
  isOpen,
  activityId,
  onClose,
  onExport,
}) => {
  const [awardScore, setAwardScore] = useState<number | undefined>(0);
  const [participationScore, setParticipationScore] = useState<number | undefined>(0);
  const [isExporting, setIsExporting] = useState(false);

  const [individualAwardMap, setIndividualAwardMap] = useReducer(
    individualAwardReducer,
    {} as HashMap<number | undefined>
  );

  const { data: activity, isLoading } = useActivityDetailsAPI<DiscussionDetails>(activityId, 'discussion');

  useEffect(() => {
    if (!activity) return;

    setAwardScore(activity.details.awardScore);
    setParticipationScore(activity.details.maxScore);

    activity.details.students.forEach((s) => {
      setIndividualAwardMap({
        type: 'SET_AWARD',
        userId: s.userId,
        award: s.awardScore,
      });
    });
  }, [activity]);

  const calculateScore = useCallback(
    (score: number, award = 0) => {
      const absoluteScore = ((participationScore || 0) * score) / 100;
      const absoluteAward = ((awardScore || 0) * award) / 100;

      const totalScore = (participationScore || 0) + (awardScore || 0);
      if (!totalScore) return 0;

      return ((absoluteScore + absoluteAward) * 100) / totalScore;
    },
    [awardScore, participationScore]
  );

  const exportActivityData = useCallback(async () => {
    setIsExporting(true);

    const particulars: { userId: string; latePenalty: number }[] = [];

    for (const [userId, award] of Object.entries(individualAwardMap)) {
      particulars.push({
        userId,
        latePenalty: award || 0,
      });
    }

    await exportActivityDetails({
      activityId,
      activityType: 'discussion',
      latePenalty: awardScore!,
      maxScore: participationScore!,
      particulars,
    });

    onExport();
  }, [activityId, individualAwardMap, awardScore, participationScore, onExport]);

  return (
    <Dialog
      variant='large'
      isOpen={isOpen}
      onClose={onClose}
      classes={{ dialog: 'activity', title: 'activity__header' }}
      title={
        <>
          <div className='activity__number'>{`Discussion ${activity?.activityNum}`}</div>
          <div className='activity__title'>{activity?.title}</div>
          <div className='activity__total'>{activity?.details.numPosts} total posts</div>
        </>
      }
      footer={
        <Button
          disabled={isExporting}
          label={isExporting ? 'Exporting...' : 'Export Scores'}
          type='ghost'
          onRipple={exportActivityData}
        />
      }
    >
      {isLoading || !activity ? (
        <div className='loader-wrapper'>
          <LoaderSpinner />
        </div>
      ) : (
        <>
          <div className='activity__details'>
            <div className='activity__detail-cell'>
              <div className='activity__detail-heading'>PUBLISHED BY</div>
              <div className='activity__detail-value'>{activity.publishedBy.name}</div>
            </div>
            <div className='activity__detail-cell'>
              <div className='activity__detail-heading'>
                <div
                  className={cn('tag', {
                    'tag--green': activity.toBeDone === 'inClass',
                    'tag--blue': activity.toBeDone === 'preClass',
                  })}
                >
                  {activity.toBeDone === 'inClass' ? 'IN CLASS' : 'PRE CLASS'}
                </div>
              </div>
              <div className='activity__detail-value'>
                {activity.dueDateTime > 0 ? dt.format(activity.dueDateTime, DATE_FORMAT) : '--'}
              </div>
            </div>
            <div className='activity__detail-cell'>
              <div className='activity__detail-heading'>PARTICIPANTS</div>
              <div className='activity__detail-value'>{activity.details.numSubmissions}</div>
            </div>
            <div className='activity__detail-cell'>
              <div className='activity__detail-heading'>TOTAL POSTS</div>
              <div className='activity__detail-value'>{activity.details.numPosts}</div>
            </div>
            <div className='activity__detail-cell'>
              <div className='activity__detail-heading'>PARTICIPATION SCORE</div>
              <div className='activity__detail-value'>
                <TextField
                  type='number'
                  wrapperClassName='activity__score-input-wrapper'
                  selector='activity__score-input'
                  value={participationScore}
                  onchange={(e) => setParticipationScore(+e.target.value)}
                  placeholder='Participation Score'
                />
              </div>
            </div>
            <div className='activity__detail-cell'>
              <div className='activity__detail-heading'>AWARD POINTS</div>
              <div className='activity__detail-value'>
                <TextField
                  type='number'
                  wrapperClassName='activity__score-input-wrapper'
                  selector='activity__score-input'
                  value={awardScore}
                  onchange={(e) => setAwardScore(+e.target.value)}
                  placeholder='Award Points'
                />
              </div>
            </div>
          </div>
          <div className='activity__body'>
            <table className='activity__table'>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Posts and Awards</th>
                  <th>Participation Score</th>
                  <th className='activity__penalty-cell'>Award Points</th>
                  <th>Final Score</th>
                </tr>
              </thead>
              <tbody>
                {activity.details.students.map((s) => (
                  <tr key={s.userId}>
                    <td>
                      <div className='activity__user'>{s.name}</div>
                    </td>
                    {s.submitted ? (
                      <>
                        <td>
                          <div className='activity__posts-and-awards'>
                            <div className='data'>
                              <ChatBubbleIcon />
                              <span>{s.numComments}</span>
                            </div>
                            {s.awardCount > 0 && (
                              <div className='data'>
                                <BadgeIcon />
                                {s.awardCount}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className={getStylesForScore(s.score)}>{toFixedIfFloat(s.score)}%</td>
                        <td
                          className={cn(
                            'activity__penalty-cell',
                            getStylesForScore(individualAwardMap[s.userId] || 0)
                          )}
                        >
                          {s.awardCount > 0 ? (
                            <PercentInput
                              wrapperClassName='activity__score-input-wrapper'
                              selector='activity__score-input'
                              initValue={individualAwardMap[s.userId]}
                              onchange={(value) =>
                                setIndividualAwardMap({
                                  type: 'SET_AWARD',
                                  userId: s.userId,
                                  award: +(value || 0),
                                })
                              }
                              placeholder='Award'
                            />
                          ) : (
                            0
                          )}
                        </td>
                        <td
                          className={getStylesForScore(calculateScore(s.score, individualAwardMap[s.userId]))}
                        >
                          {toFixedIfFloat(calculateScore(s.score, individualAwardMap[s.userId]))}%
                        </td>
                      </>
                    ) : (
                      <>
                        <td className='light-grey'>
                          <div className='activity__posts-and-awards'>
                            <div className='data'>
                              <ChatBubbleIcon />
                              <span>0</span>
                            </div>
                          </div>
                        </td>
                        <td className='light-grey'>0%</td>
                        <td className='light-grey'>0</td>
                        <td className='light-grey'>0%</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Dialog>
  );
};

export default DiscussionParticipations;
